/* ============================================================
   Trasse Families — connecting to your child's school.

   The household's half of the one cross-org arrangement in the product. What
   this panel has to get right is not the mechanics but the EXPECTATION: a
   parent who asks and hears nothing back assumes the app is broken, so a
   pending request says plainly that the school has to answer, and a declined
   one says so without dressing it up.

   The copy is also careful about what a link is not. A parent reasonably fears
   that connecting to a school hands over their car's live position. It does
   not: the arrangement lets the school put their child on a school trip without
   an invite code, and nothing else travels.
   ============================================================ */
import React, { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  FamilySchoolLink, listSchoolLinks, requestSchoolLink, respondToSchoolLink,
} from '../../services/paymentsApi';
import { FAM, DISPLAY_FONT } from './familyTheme';

interface Props { orgCode: string; schoolName?: string }

const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section style={{
    background: '#fff', border: `1px solid ${FAM.line}`, borderRadius: 20,
    padding: 'clamp(18px,3vw,26px)',
  }}>
    {children}
  </section>
);

const quietBtn: React.CSSProperties = {
  padding: '9px 15px', borderRadius: 10, border: `1px solid ${FAM.line}`,
  background: '#fff', color: FAM.ink2, font: 'inherit', fontSize: 13.5,
  fontWeight: 600, cursor: 'pointer',
};

/** Only the states a household can actually be in, in its own words. */
function statusCopy(link: FamilySchoolLink): { chip: string; tone: string; line: string } {
  switch (link.status) {
    case 'accepted':
      return {
        chip: 'CONNECTED',
        tone: FAM.pos,
        line: `${link.schoolName || link.schoolCode} can add your child to their school trips without an invite code. Those trips show up on your Today screen.`,
      };
    case 'requested':
      return {
        chip: 'WAITING ON THE SCHOOL',
        tone: FAM.amber,
        line: 'They have your request. Only the school can accept it, so there is nothing further for you to do here — we will show it as connected the moment they do.',
      };
    case 'declined':
      return {
        chip: 'NOT ACCEPTED',
        tone: FAM.ink3,
        line: 'The school did not accept this request. If you think that was a mistake, speak to them and ask again below.',
      };
    default:
      return {
        chip: link.revokedBy === 'school' ? 'ENDED BY THE SCHOOL' : 'DISCONNECTED',
        tone: FAM.muted,
        line: link.revokedBy === 'school'
          ? 'The school ended this arrangement. Your household and its runs are unaffected.'
          : 'You ended this arrangement. Your household and its runs are unaffected.',
      };
  }
}

export default function FamilySchoolLinkPanel({ orgCode, schoolName }: Props) {
  const [links, setLinks] = useState<FamilySchoolLink[]>([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async () => {
    if (!orgCode) return;
    const res = await listSchoolLinks(orgCode);
    if (res.ok) setLinks(res.links);
  }, [orgCode]);

  useEffect(() => { load(); }, [load]);

  const live = links.filter((l) => l.status === 'accepted' || l.status === 'requested');
  const past = links.filter((l) => l.status === 'declined' || l.status === 'revoked');

  const send = async () => {
    setErr(''); setOk('');
    const school = code.trim();
    if (!school) { setErr("Enter the school's Trasse code."); return; }
    setBusy(true);
    /* The children being offered have to travel WITH the request.
       childIds is the consent list, and addLinkedChildToTrip refuses any child
       that is not on it — so a request sent without one produced a link the
       school could accept and then never use for anybody, with nothing on
       either side saying why. The app has always sent its roster; only the web
       panel omitted it (review, 09-06). */
    let childIds: string[] = [];
    try {
      const snap = await getDocs(query(collection(db, 'children'), where('orgCode', '==', orgCode)));
      childIds = snap.docs.map((d) => d.id);
    } catch {
      setBusy(false);
      setErr('We could not read your children just now. Try again in a moment.');
      return;
    }
    if (childIds.length === 0) {
      setBusy(false);
      setErr('Add your child to the household before connecting to a school.');
      return;
    }
    const res = await requestSchoolLink({ orgCode, schoolCode: school, childIds });
    setBusy(false);
    if (!res.ok) { setErr(res.message || 'We could not send that request.'); return; }
    setCode('');
    setOk(`Sent to ${res.schoolName || school}. They have to accept it before anything is shared.`);
    load();
  };

  const act = async (linkId: string, action: 'revoke') => {
    setErr(''); setOk(''); setBusy(true);
    const res = await respondToSchoolLink(linkId, action);
    setBusy(false);
    if (!res.ok) { setErr(res.message || 'That did not go through.'); return; }
    load();
  };

  return (
    <Panel>
      <h2 style={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 30, letterSpacing: '-.01em', margin: 0 }}>
        Your child&apos;s school
      </h2>
      <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: FAM.ink2, maxWidth: 580 }}>
        Connect to {schoolName ? schoolName : "your child's school"} and they can add your child to a
        school trip without you chasing an invite code — a match, an outing, the end-of-term run.
      </p>

      <p style={{
        margin: '12px 0 0', padding: '11px 14px', borderRadius: 12,
        background: FAM.tint, color: FAM.ink2, fontSize: 13.5, lineHeight: 1.6, maxWidth: 580,
      }}>
        <b style={{ color: FAM.ink }}>What a school does not get:</b> your car&apos;s live position,
        your driver, your billing, or any run they did not organise themselves. A trip they run is a
        trip they can see. Everything else stays inside your household.
      </p>

      {live.length > 0 && (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {live.map((l) => {
            const s = statusCopy(l);
            return (
              <div key={l.linkId} style={{
                padding: '14px 16px', borderRadius: 14,
                background: l.status === 'accepted' ? FAM.tint : '#FAF9F6',
                border: `1px solid ${l.status === 'accepted' ? FAM.tint2 : FAM.line}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 15.5, fontWeight: 700, color: FAM.ink, flex: '1 1 auto', minWidth: 0 }}>
                    {l.schoolName || l.schoolCode}
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '.04em', color: s.tone,
                  }}>{s.chip}</span>
                </div>
                <p style={{ margin: '7px 0 0', fontSize: 13.5, lineHeight: 1.55, color: FAM.ink2 }}>
                  {s.line}
                </p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act(l.linkId, 'revoke')}
                  style={{ ...quietBtn, marginTop: 12 }}
                >
                  {l.status === 'accepted' ? 'Disconnect' : 'Withdraw the request'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Asking is only offered when nothing is live — a household has one
          school at a time, and two pending requests is a support ticket. */}
      {live.length === 0 && (
        <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '1 1 220px', minWidth: 0 }}>
            <span style={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
              textTransform: 'uppercase', color: FAM.ink3,
            }}>The school&apos;s Trasse code</span>
            <input
              value={code}
              onChange={(e) => { setCode(e.target.value); setErr(''); }}
              placeholder="RIVERSIDE.bus"
              style={{
                font: 'inherit', fontSize: 14, padding: '9px 11px', borderRadius: 10,
                border: `1px solid ${FAM.line}`, background: '#fff', color: FAM.ink, minWidth: 0,
              }}
            />
          </label>
          <button
            type="button"
            disabled={busy}
            onClick={send}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: busy ? FAM.accentPale : FAM.accent,
              color: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Sending…' : 'Ask to connect'}
          </button>
        </div>
      )}

      {live.length === 0 && (
        <p style={{ margin: '10px 0 0', fontSize: 12.5, color: FAM.muted, lineHeight: 1.55 }}>
          The school&apos;s code is on anything Trasse has sent you from them, or ask the school
          office for it.
        </p>
      )}

      {err && (
        <p style={{
          margin: '12px 0 0', padding: '10px 13px', borderRadius: 10,
          background: FAM.dangerTint, color: FAM.danger, fontSize: 13.5, lineHeight: 1.55,
        }}>{err}</p>
      )}
      {ok && (
        <p style={{
          margin: '12px 0 0', padding: '10px 13px', borderRadius: 10,
          background: FAM.posTint, color: FAM.pos, fontSize: 13.5, lineHeight: 1.55,
        }}>{ok}</p>
      )}

      {past.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.08em',
            textTransform: 'uppercase', color: FAM.muted, marginBottom: 8,
          }}>Previously</div>
          {past.slice(0, 4).map((l) => {
            const s = statusCopy(l);
            return (
              <div key={l.linkId} style={{
                display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap',
                padding: '9px 13px', borderRadius: 10, border: `1px solid ${FAM.line}`, marginBottom: 6,
              }}>
                <span style={{ fontSize: 14, color: FAM.ink2, flex: '1 1 auto', minWidth: 0 }}>
                  {l.schoolName || l.schoolCode}
                </span>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', color: s.tone }}>
                  {s.chip}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
